import React, { useEffect, useMemo, useState } from 'react'
import { FolderIcon } from '@heroicons/react/24/outline'
import Button from '../../ui/Button'
import AlxModalShell from './AlxModalShell'
import HelpTooltipButton from './HelpTooltipButton'
import PassMappingPanel from './PassMappingPanel'
import AlxBatchPassMappingAccordion from './AlxBatchPassMappingAccordion'
import { groupFilesByAlxWeldId } from '../../../utils/alx/weldIdFromPath'
import type { BatchWeldDraft } from '../../../utils/alx/alxUploadPipeline'
import { prepareAlxUploadForFiles } from '../../../utils/alx/alxUploadPipeline'
import type { AlxPassRow, CwCcwMode } from '../../../utils/alx/types'
import { validatePassMappings } from '../../../utils/weldUpload/validatePassMappings'
import { countLearnedPrefills } from '../../../utils/weldUpload/passMappingAutofill'
import type { UploadFormat } from '../../../utils/weldUpload/types'
import type { EmbeddedFooterActions } from './embeddedFooterActions'

export type AlxMultiWeldWizardStep = 'choice' | 'pickOne' | 'batchRename' | 'singleRename'

export interface AlxSingleRenameDraft {
  weldIdLabel: string
  files: File[]
  rows: AlxPassRow[]
  passMappings: Record<string, string>
  learnedSuggestions?: Record<string, string>
  distinctPassNames?: string[]
}

function draftPassNamesFromRows(
  rows: AlxPassRow[],
  distinctPassNames?: string[]
): string[] {
  if (distinctPassNames && distinctPassNames.length > 0) {
    return [...distinctPassNames].sort()
  }
  return Array.from(new Set(rows.map((r) => r.passName))).sort()
}

function batchDraftPassNames(draft: BatchWeldDraft): string[] {
  if (draft.distinctPassNames && draft.distinctPassNames.length > 0) {
    return [...draft.distinctPassNames].sort()
  }
  return Array.from(new Set(draft.rows.map((r) => r.passName))).sort()
}

export interface AlxMultiWeldIdDialogProps {
  open: boolean
  weldIds: string[]
  files: File[]
  cwCcwMode: CwCcwMode
  wpsPassLabels: string[]
  busy?: boolean
  onClose: () => void
  onPrepareBatch: (groups: Map<string, File[]>, cwCcwMode: CwCcwMode) => Promise<BatchWeldDraft[]>
  onFinalizeBatch: (drafts: BatchWeldDraft[]) => Promise<void>
  onFinalizeSingle: (draft: AlxSingleRenameDraft) => Promise<void>
  onApplyWeldIdForPickOne?: (weldIdLabel: string) => void
  allowCreateMultiple?: boolean
  uploadFormat?: UploadFormat
  groupFilesByWeldId?: (files: File[]) => Map<string, File[]>
  prepareSingleForFiles?: (files: File[], weldIdLabel: string) => Promise<AlxSingleRenameDraft>
  passSourceLabel?: string
  embedded?: boolean
  onFooterActionsChange?: (actions: EmbeddedFooterActions | null) => void
}

const CHOICE_HELP = {
  createMany: {
    title: 'Create multiple Floor Sheet welds',
    description:
      'Creates one Floor Sheet row per detected weld ID (copies bevel and Pup 1/2 from this row), then opens Rename Pass for each weld before import.',
    hoverHint: 'Create one row per weld ID'
  },
  pickOne: {
    title: 'Use one weld ID',
    description:
      'Imports only the selected subfolder into the current row, then opens Rename Pass so you can map ALX passes to WPS labels.',
    hoverHint: 'Import a single weld folder'
  },
  cancel: {
    title: 'Cancel',
    description: 'Closes this wizard without creating welds or uploading data.',
    hoverHint: 'Close without changes'
  }
} as const

const ALX_CHOICE_HELP = {
  createMany: {
    title: 'Create multiple Floor Sheet welds',
    description:
      'Creates one Floor Sheet row per detected weld ID (copies bevel and Pup 1/2 from this row), then imports each weld using pass names from the logs.',
    hoverHint: 'Create one row per weld ID'
  },
  pickOne: {
    title: 'Use one weld ID',
    description:
      'Imports only the selected subfolder into the current row. Pass names from the logs are used as-is.',
    hoverHint: 'Import a single weld folder'
  },
  cancel: CHOICE_HELP.cancel
} as const

function ChoiceActionButton({
  label,
  help,
  variant = 'primary',
  testId,
  onClick,
  disabled
}: {
  label: string
  help: { title: string; description: string; hoverHint?: string }
  variant?: 'primary' | 'secondary'
  testId: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <div className="relative min-w-0 flex-1">
      <Button
        type="button"
        variant={variant === 'primary' ? 'primary' : 'secondary'}
        className="h-full w-full whitespace-normal px-3 py-3 text-center text-sm leading-snug"
        data-testid={testId}
        onClick={onClick}
        disabled={disabled}
      >
        {label}
      </Button>
      <HelpTooltipButton
        title={help.title}
        description={help.description}
        hoverHint={help.hoverHint}
      />
    </div>
  )
}

export default function AlxMultiWeldIdDialog({
  open,
  weldIds,
  files,
  cwCcwMode,
  wpsPassLabels,
  busy = false,
  onClose,
  onPrepareBatch,
  onFinalizeBatch,
  onFinalizeSingle,
  onApplyWeldIdForPickOne,
  allowCreateMultiple = true,
  uploadFormat = 'alx-summary',
  groupFilesByWeldId,
  prepareSingleForFiles,
  passSourceLabel,
  embedded = false,
  onFooterActionsChange
}: AlxMultiWeldIdDialogProps) {
  const [step, setStep] = useState<AlxMultiWeldWizardStep>('choice')
  const [selectedId, setSelectedId] = useState('')
  const [batchDrafts, setBatchDrafts] = useState<BatchWeldDraft[]>([])
  const [singleDraft, setSingleDraft] = useState<AlxSingleRenameDraft | null>(null)
  const [localError, setLocalError] = useState<string | null>(null)
  const [preparing, setPreparing] = useState(false)

  useEffect(() => {
    if (!open) {
      setStep('choice')
      setSelectedId('')
      setBatchDrafts([])
      setSingleDraft(null)
      setLocalError(null)
      setPreparing(false)
    }
  }, [open])

  const sortedIds = useMemo(() => [...weldIds].sort(), [weldIds])
  const resolvedPassSourceLabel =
    passSourceLabel ?? (uploadFormat === 'telemetry-txt' ? 'TXT pass' : 'ALX pass')
  const isTelemetry = uploadFormat === 'telemetry-txt'
  const choiceHelp = isTelemetry ? CHOICE_HELP : ALX_CHOICE_HELP

  const resolveGroups = (fileList: File[]) =>
    groupFilesByWeldId
      ? groupFilesByWeldId(fileList)
      : groupFilesByAlxWeldId(fileList, cwCcwMode)

  const singleRawPassNames = useMemo(
    () =>
      singleDraft
        ? draftPassNamesFromRows(singleDraft.rows, singleDraft.distinctPassNames)
        : [],
    [singleDraft]
  )

  const singleMappingValid = useMemo(
    () =>
      singleDraft
        ? validatePassMappings(singleRawPassNames, singleDraft.passMappings).valid
        : false,
    [singleDraft, singleRawPassNames]
  )

  const singleLearnedPrefillCount = useMemo(() => {
    if (!singleDraft?.learnedSuggestions) return 0
    return countLearnedPrefills(
      singleRawPassNames,
      singleDraft.passMappings,
      singleDraft.learnedSuggestions,
      wpsPassLabels
    )
  }, [singleDraft, singleRawPassNames, wpsPassLabels])

  const batchAllMapped = useMemo(
    () =>
      batchDrafts.length > 0 &&
      batchDrafts.every((d) => {
        const names = batchDraftPassNames(d)
        return validatePassMappings(names, d.passMappings).valid
      }),
    [batchDrafts]
  )

  function handleClose() {
    setStep('choice')
    setSelectedId('')
    setBatchDrafts([])
    setSingleDraft(null)
    setLocalError(null)
    onClose()
  }

  async function startBatchRename() {
    setLocalError(null)
    setPreparing(true)
    try {
      const groups = resolveGroups(files)
      const drafts = await onPrepareBatch(groups, cwCcwMode)
      setBatchDrafts(drafts ?? [])
      setStep('batchRename')
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to prepare batch upload')
    } finally {
      setPreparing(false)
    }
  }

  async function continuePickOne() {
    if (!selectedId) return
    setLocalError(null)
    setPreparing(true)
    try {
      const groups = resolveGroups(files)
      const subset = groups.get(selectedId) ?? []
      if (prepareSingleForFiles) {
        const draft = await prepareSingleForFiles(subset, selectedId)
        onApplyWeldIdForPickOne?.(selectedId)
        setSingleDraft(draft)
        setStep('singleRename')
        return
      }
      const prepared = await prepareAlxUploadForFiles({
        files: subset,
        cwCcwMode,
        wpsPassLabels
      })
      onApplyWeldIdForPickOne?.(selectedId)
      setSingleDraft({
        weldIdLabel: selectedId,
        files: subset,
        rows: prepared.rows ?? [],
        passMappings: prepared.passMappings ?? {},
        learnedSuggestions: prepared.learnedSuggestions
      })
      setStep('singleRename')
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to prepare weld data')
    } finally {
      setPreparing(false)
    }
  }

  async function submitBatch() {
    if (!batchAllMapped) return
    setLocalError(null)
    setPreparing(true)
    try {
      await onFinalizeBatch(batchDrafts)
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Batch import failed')
      setPreparing(false)
    }
  }

  async function submitSingle() {
    if (!singleDraft || !singleMappingValid) return
    setLocalError(null)
    setPreparing(true)
    try {
      await onFinalizeSingle(singleDraft)
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Import failed')
      setPreparing(false)
    }
  }

  const isBusy = busy || preparing

  const titles: Record<AlxMultiWeldWizardStep, { title: string; subtitle: string }> = {
    choice: {
      title: 'Multiple weld IDs detected',
      subtitle: isTelemetry
        ? 'These files contain data for more than one Weld #. Choose how to proceed.'
        : 'This folder contains data for more than one weld. Choose how to proceed.'
    },
    pickOne: {
      title: 'Use one weld ID',
      subtitle: isTelemetry
        ? 'Select which Weld # to import into the current Floor Sheet row.'
        : 'Select which weld folder to import into the current Floor Sheet row.'
    },
    batchRename: {
      title: isTelemetry ? 'Rename Pass — multiple welds' : 'Import multiple welds',
      subtitle: isTelemetry
        ? 'Map TXT passes to WPS labels for each weld before import.'
        : 'ALX pass names from the logs are used as-is on the parameters table.'
    },
    singleRename: {
      title: isTelemetry ? 'Rename Pass' : 'Import weld',
      subtitle: isTelemetry
        ? 'Map each TXT pass to a WPS label, then import this weld.'
        : 'ALX pass names from the logs are used as-is on the parameters table.'
    }
  }

  const { title, subtitle } = titles[step]

  const footer = (() => {
    if (step === 'choice') {
      return (
        <div className="flex flex-row gap-3">
          {allowCreateMultiple ? (
            <ChoiceActionButton
              label="Create multiple welds"
              help={choiceHelp.createMany}
              testId="alx-multi-weld-create-many"
              onClick={() => void startBatchRename()}
              disabled={isBusy}
            />
          ) : null}
          <ChoiceActionButton
            label="Use one weld ID"
            help={choiceHelp.pickOne}
            variant="secondary"
            testId="alx-multi-weld-pick-one"
            onClick={() => setStep('pickOne')}
            disabled={isBusy}
          />
          <ChoiceActionButton
            label="Cancel"
            help={choiceHelp.cancel}
            variant="secondary"
            testId="alx-multi-weld-cancel"
            onClick={handleClose}
            disabled={isBusy}
          />
        </div>
      )
    }
    if (step === 'pickOne') {
      return (
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => setStep('choice')} disabled={isBusy}>
            Back
          </Button>
          <Button
            type="button"
            data-testid="alx-multi-weld-continue-pick-one"
            disabled={!selectedId || isBusy}
            onClick={() => void continuePickOne()}
          >
            {isBusy
              ? 'Loading…'
              : isTelemetry
                ? 'Continue to Rename Pass'
                : 'Continue'}
          </Button>
        </div>
      )
    }
    if (step === 'batchRename') {
      return (
        <div className="flex items-center justify-between gap-3">
          <Button type="button" variant="secondary" onClick={() => setStep('choice')} disabled={isBusy}>
            Back
          </Button>
          <Button
            type="button"
            data-testid="alx-multi-weld-import-all"
            disabled={!batchAllMapped || isBusy}
            onClick={() => void submitBatch()}
          >
            {isBusy ? 'Importing…' : `Import all ${batchDrafts.length} welds`}
          </Button>
        </div>
      )
    }
    return (
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => setStep('pickOne')}
          disabled={isBusy}
        >
          Back
        </Button>
        <Button
          type="button"
          data-testid="alx-multi-weld-import-single"
          disabled={!singleMappingValid || isBusy}
          onClick={() => void submitSingle()}
        >
          {isBusy ? 'Importing…' : 'Import weld'}
        </Button>
      </div>
    )
  })()

  useEffect(() => {
    if (!embedded || !open || !onFooterActionsChange) return
    onFooterActionsChange({
      canUpload: false,
      busy: isBusy,
      label: 'Upload',
      onUpload: () => {},
      customFooter: footer
    })
    return () => onFooterActionsChange(null)
  }, [embedded, open, onFooterActionsChange, isBusy, footer, step, batchAllMapped, singleMappingValid, selectedId, batchDrafts.length])

  const wizardBody = (
    <div className="space-y-5 text-sm text-text-grey">
      {preparing && (step === 'choice' || step === 'pickOne') ? (
        <div
          className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600"
          data-testid="alx-pass-mapping-preparing"
          role="status"
        >
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-r-transparent" />
          Loading suggested pass mappings…
        </div>
      ) : null}
      {step === 'choice' ? (
        <div>
          <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-heritage-grey">
            Detected weld IDs
          </p>
          <div className="flex flex-wrap gap-2" data-testid="alx-multi-weld-id-list">
            {sortedIds.map((id) => (
              <span
                key={id}
                className="inline-flex rounded-full bg-blueprint-blue/10 px-3 py-1 text-xs font-semibold text-blueprint-blue ring-1 ring-inset ring-blueprint-blue/20"
              >
                {id}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {step === 'pickOne' ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {sortedIds.map((id) => {
            const selected = selectedId === id
            return (
              <button
                key={id}
                type="button"
                data-testid={`alx-multi-weld-id-card-${id}`}
                className={`rounded-xl border-2 px-4 py-4 text-left transition-colors ${
                  selected
                    ? 'border-blueprint-blue bg-blueprint-blue/5 ring-2 ring-blueprint-blue/20'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => setSelectedId(id)}
              >
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  Weld ID
                </p>
                <p className="mt-1 text-base font-semibold text-gray-900">{id}</p>
              </button>
            )
          })}
        </div>
      ) : null}

      {step === 'batchRename' ? (
        isTelemetry ? (
          <AlxBatchPassMappingAccordion
            drafts={batchDrafts}
            wpsPassLabels={wpsPassLabels}
            disabled={isBusy}
            passSourceLabel={resolvedPassSourceLabel}
            onDraftMappingsChange={(rowId, passMappings) =>
              setBatchDrafts((prev) =>
                prev.map((d) => (d.rowId === rowId ? { ...d, passMappings } : d))
              )
            }
          />
        ) : (
          <div data-testid="alx-batch-ready-list" className="space-y-2">
            <p className="text-[11px] text-heritage-grey">
              {batchDrafts.length} weld{batchDrafts.length === 1 ? '' : 's'} ready to import. Pass
              names from the logs are used as-is.
            </p>
            <div className="flex flex-wrap gap-2">
              {batchDrafts.map((draft) => (
                <span
                  key={draft.rowId}
                  className="inline-flex rounded-full bg-blueprint-blue/10 px-3 py-1 text-xs font-semibold text-blueprint-blue ring-1 ring-inset ring-blueprint-blue/20"
                >
                  {draft.weldIdLabel}
                </span>
              ))}
            </div>
          </div>
        )
      ) : null}

      {step === 'singleRename' && singleDraft ? (
        <>
          <div className="rounded-lg border border-blueprint-blue/20 bg-blueprint-blue/5 px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-heritage-grey">
              Weld ID
            </p>
            <p
              className="text-base font-semibold text-blueprint-blue"
              data-testid="alx-single-rename-weld-id"
            >
              {singleDraft.weldIdLabel}
            </p>
          </div>
          {isTelemetry ? (
            <PassMappingPanel
              rawPassNames={singleRawPassNames}
              passMappings={singleDraft.passMappings}
              wpsPassLabels={wpsPassLabels}
              learnedPrefillCount={singleLearnedPrefillCount}
              onMappingChange={(raw, canonical) =>
                setSingleDraft((prev) =>
                  prev
                    ? { ...prev, passMappings: { ...prev.passMappings, [raw]: canonical } }
                    : prev
                )
              }
              disabled={isBusy}
              sourceLabel={resolvedPassSourceLabel}
            />
          ) : (
            <p className="text-[11px] text-heritage-grey">
              Pass names from the logs are used as-is on the parameters table.
            </p>
          )}
        </>
      ) : null}

      {localError ? (
        <p role="alert" className="text-sm text-error-600">
          {localError}
        </p>
      ) : null}
    </div>
  )

  if (embedded) {
    if (!open) return null
    return (
      <div className="space-y-4" data-testid="alx-multi-weld-id-embedded">
        <div>
          <h3 className="font-heading text-base font-semibold text-blueprint-blue">{title}</h3>
          <p className="mt-1 text-xs text-heritage-grey">{subtitle}</p>
        </div>
        {wizardBody}
      </div>
    )
  }

  return (
    <AlxModalShell
      open={open}
      onClose={handleClose}
      title={title}
      subtitle={subtitle}
      icon={<FolderIcon className="h-5 w-5" />}
      width={step === 'batchRename' || step === 'singleRename' ? 'lg' : 'md'}
      zIndex={10010}
      testId="alx-multi-weld-id-dialog"
      titleId="alx-multi-weld-id-title"
      closeDisabled={isBusy}
      footer={footer}
    >
      {wizardBody}
    </AlxModalShell>
  )
}
